import { Vals } from '~lib';

export namespace User {
  /**
   * @desc WorkOS environment-level role slugs. A membership carries either exactly one
   * internal-group role (staff, cross-deployment, no pending state) or product-group roles (at
   * most one per product group, independently combinable and discrete — no role implies another),
   * with the permissions a token bears being the union across the assigned roles
   * (`refactor-roles-internal-product-split`, superseding design D4 of H2-171). Staff may also
   * hold one role in the `ario` group beside their internal role (ADR-29).
   */
  export const roles = {
    InternalUser: 'internal-user',
    /** @desc Baseline admin role: the admin panel read-only, without any admin write (D21). */
    InternalAdminViewer: 'internal-admin-viewer',
    InternalAdmin: 'internal-admin',
    AgentPending: 'agent-pending',
    AgentBeta: 'agent-beta',
    /** @desc Reserved for the platform beta phase — declared, not yet assigned by app flows. */
    PlatformPending: 'platform-pending',
    /** @desc Reserved for the platform beta phase — declared, not yet assigned by app flows. */
    PlatformBeta: 'platform-beta',
    /** @desc Reserved final-launch tier-1 licence — declared, not yet assigned by app flows. */
    PlatformBasic: 'platform-basic',
    /** @desc Reserved final-launch tier-2 licence — declared, not yet assigned by app flows. */
    PlatformPremium: 'platform-premium',
    /** @desc Ario read-only: conversations, workflows, runs, schedules and outputs (ADR-29). */
    ArioViewer: 'ario-viewer',
    /** @desc Ario in full: prompt, author, accept, run and schedule (ADR-29). */
    ArioOperator: 'ario-operator',
  } as const;
  export type Role = Vals<typeof roles>;

  /**
   * @desc Permission slugs attached to the roles above. These — never role slugs — are what the
   * guards consult, so a role can be renamed or split without touching an enforcement point.
   */
  export const permissions = {
    AgentAccess: 'agent:access',
    PlatformAccess: 'platform:access',
    /** @desc The admin panel and its read procedures; carried by every admin role (D21). */
    AdminView: 'admin:view',
    /** @desc Every admin write. Never implies any Ario access (ADR-29, decision 8). */
    AdminAccess: 'admin:access',
    /** @desc `Automation.permissions.Read` — Ario reads (ADR-29). */
    AutomationsRead: 'automations:read',
    /** @desc `Automation.permissions.Write` — Ario authoring, prompts and card answers. */
    AutomationsWrite: 'automations:write',
    /** @desc `Automation.permissions.Run` — Ario runs. */
    AutomationsRun: 'automations:run',
  } as const;
  export type Permission = Vals<typeof permissions>;

  /**
   * @desc The role groups. The internal group is exclusive of the product groups: a membership
   * carries either exactly one internal-group role or product-group roles (one per group at most).
   * The `ario` group is neither: it is staff-only and combines with an internal role carrying
   * `admin:view`, never with a product role (ADR-29, D21).
   */
  export const roleGroups = {
    Internal: 'internal',
    Agent: 'agent',
    Platform: 'platform',
    Ario: 'ario',
  } as const;
  export type RoleGroup = Vals<typeof roleGroups>;

  /** @desc The product groups — the ones the internal group is exclusive of. */
  export const productRoleGroups: readonly RoleGroup[] = [roleGroups.Agent, roleGroups.Platform];

  /**
   * @desc Role slugs belonging to each group, in ascending order of access. Used by the WorkOS
   * gateway to replace a membership's role within one group without disturbing the others.
   */
  export const rolesByGroup: Record<RoleGroup, readonly Role[]> = {
    [roleGroups.Internal]: [roles.InternalUser, roles.InternalAdminViewer, roles.InternalAdmin],
    [roleGroups.Agent]: [roles.AgentPending, roles.AgentBeta],
    [roleGroups.Platform]: [
      roles.PlatformPending,
      roles.PlatformBeta,
      roles.PlatformBasic,
      roles.PlatformPremium,
    ],
    [roleGroups.Ario]: [roles.ArioViewer, roles.ArioOperator],
  };

  /**
   * @desc The permissions each staff role carries, as defined in the WorkOS environments (where
   * they are created by slug). WorkOS stays the system of record and the token's permission claim
   * is what the guards read; this mirror exists so the gateway can apply the admin-view
   * prerequisite of an `ario` role to a membership's role slugs, which carry no permissions.
   */
  export const staffRolePermissions = {
    [roles.InternalUser]: [permissions.AgentAccess, permissions.PlatformAccess],
    [roles.InternalAdminViewer]: [
      permissions.AgentAccess,
      permissions.PlatformAccess,
      permissions.AdminView,
    ],
    [roles.InternalAdmin]: [
      permissions.AgentAccess,
      permissions.PlatformAccess,
      permissions.AdminView,
      permissions.AdminAccess,
    ],
    [roles.ArioViewer]: [permissions.AutomationsRead],
    [roles.ArioOperator]: [
      permissions.AutomationsRead,
      permissions.AutomationsWrite,
      permissions.AutomationsRun,
    ],
  } as const satisfies Partial<Record<Role, readonly Permission[]>>;

  /** @desc The group a role slug belongs to, or `null` for a slug outside every group. */
  export const roleGroupOf = (slug: string): RoleGroup | null => {
    for (const group of Object.values(roleGroups)) {
      if (rolesByGroup[group].some((role) => role === slug)) return group;
    }
    return null;
  };

  /** @desc Type guard for a slug that is a member of the given group. */
  export const isRoleInGroup = (slug: string, group: RoleGroup): slug is Role =>
    roleGroupOf(slug) === group;

  /** @desc Whether a role slug carries the permission, per {@link staffRolePermissions}. */
  export const roleCarries = (slug: string | null | undefined, permission: Permission): boolean => {
    const byRole: Partial<Record<string, readonly Permission[]>> = staffRolePermissions;
    return slug ? (byRole[slug]?.includes(permission) ?? false) : false;
  };

  /**
   * @desc Whether a role slug is an internal role carrying `admin:view` — the prerequisite of an
   * `ario` role (D21). `false` for every other slug, including `internal-user`.
   */
  export const carriesAdminView = (slug: string | null | undefined): boolean =>
    !!slug && isRoleInGroup(slug, roleGroups.Internal) && roleCarries(slug, permissions.AdminView);

  /**
   * @desc The one role a membership holds in the given group, or `null` when it carries none (a
   * membership with no platform-group role has no platform state at all — design D4).
   */
  export const roleInGroup = (slugs: string[], group: RoleGroup): Role | null =>
    slugs.find((slug): slug is Role => isRoleInGroup(slug, group)) ?? null;

  export type Model<D extends string | Date = Date> = {
    _id: string;
    email: string;
    name: {
      first: string;
      last: string;
      formatted: string;
    };
    roles: Role[];
    createdAt: D;
    updatedAt: D;
  };

  /**
   * @desc A row of the admin user-management panel: the WorkOS user, the organisation whose
   * membership carries their roles, and the role each product group is set to.
   *
   * Derived per request from WorkOS (the system of record for membership and roles) — never from
   * the Mongo `users` document, whose `roles` field is data rather than an enforcement point.
   */
  export type Managed = {
    workosUserId: string;
    /**
     * @desc The portal's internal user id — the id keying the Mongo `users` collection and
     * `conversation.refs.userId` (the token's `external_id` claim when set, otherwise the WorkOS
     * `sub`; see `apps/backend/src/lib/workos.ts`). Undefined when no portal user document has
     * been resolved for this membership yet, i.e. the person has never signed in.
     */
    userId?: string;
    /** @desc The WorkOS `external_id` (the legacy internal id for migrated users), when set. */
    externalId?: string;
    membershipId: string;
    email: string;
    name: string | null;
    organisation: { id: string; name: string };
    /** @desc The single staff role, exclusive of the product roles below; `null` for externals. */
    internalRole: Role | null;
    /** @desc Every external membership carries one; `null` for staff (internal role instead). */
    agentRole: Role | null;
    /** @desc `null` means no platform state — the ordinary case for a subscriber. */
    platformRole: Role | null;
    /** @desc The staff member's Ario role (ADR-29); `null` for no Ario access and for externals. */
    arioRole: Role | null;
    /** @desc Slugs outside every group, e.g. the WorkOS default `member`. */
    otherRoles: string[];
    /** @desc WorkOS membership status (`active` | `inactive` | `pending`). */
    status: string;
    createdAt: string;
  };

  /**
   * @desc A pending internal-organisation invitation: sent but not yet accepted. Surfaced on the
   * admin Team page so invited staff are visible before onboarding completes.
   */
  export type PendingInvitation = {
    invitationId: string;
    email: string;
    organisation: { id: string; name: string };
    /** @desc The internal role the invitee will hold on acceptance; `null` if it was changed to
     * something outside the internal group after sending. */
    role: Role | null;
    expiresAt: string;
    createdAt: string;
  };

  /**
   * @desc A subscriber-organisation invitation that lapsed before it was accepted and has not
   * been superseded by a newer one for the same address. WorkOS can only resend a pending
   * invitation, so the admin Subscribers page offers a reissue (a fresh invitation) instead.
   */
  export type ExpiredInvitation = {
    invitationId: string;
    email: string;
    organisation: { id: string; name: string };
    /** @desc The role slug the invitee would have joined on; carried over on reissue. */
    roleSlug: string | null;
    expiresAt: string;
    createdAt: string;
  };

  export const toSerial = (u: Model): Model<string> => ({
    ...u,
    createdAt: u.createdAt.toISOString(),
    updatedAt: u.updatedAt.toISOString(),
  });
}
